import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FormField, Input, ZagButton, AppStatusComponent } from '@zag/ui';
import { AuthService } from './auth.service';

/**
 * Self-service "forgot password" page.
 *
 * Collects an email and asks better-auth to email a reset link. To avoid
 * leaking which emails have accounts, a generic confirmation is shown on any
 * successful submit regardless of whether the email matched a user.
 */
@Component({
  selector: 'sc-forgot-password',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ReactiveFormsModule, TranslatePipe, FormField, Input, ZagButton, AppStatusComponent],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.forgotPasswordPage.title' | translate }}</span>
        </h2>

        @if (submitted()) {
          <p class="auth-confirmation" role="status" aria-live="polite">
            {{ 'auth.forgotPasswordPage.confirmation' | translate }}
          </p>
        } @else {
          @if (authService.error()) {
            <div class="error-message">{{ authService.error() }}</div>
          }

          <p class="auth-description">{{ 'auth.forgotPasswordPage.description' | translate }}</p>

          <form [formGroup]="forgotForm" (ngSubmit)="onSubmit()" method="post">
            <div class="form-fields">
              <zag-form-field
                [label]="'auth.forgotPasswordPage.emailLabel' | translate"
                [error]="forgotForm.get('email')?.touched && forgotForm.get('email')?.invalid ? ('errors.invalidEmail' | translate) : ''"
                [required]="true"
                forId="forgot-email"
              >
                <zag-input
                  id="forgot-email"
                  formControlName="email"
                  type="email"
                  [placeholder]="'auth.emailPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <button zagButton zagType="primary" type="submit" [disabled]="authService.isLoading()" class="submit-button">
                {{ 'auth.forgotPasswordPage.submit' | translate }}
              </button>
            </div>
          </form>
        }

        <div class="auth-footer">
          <a routerLink="/login" class="link">{{ 'auth.forgotPasswordPage.backToLogin' | translate }}</a>
        </div>
      </div>
      <footer class="auth-status-footer">
        <zag-app-status />
      </footer>
    </div>
  `,
  styles: [
    `
      .auth-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: var(--spacing-md);
        height: 100dvh;
        box-sizing: border-box;
        overflow: hidden;
      }

      .auth-status-footer {
        position: absolute;
        bottom: var(--spacing-md);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
        max-width: 400px;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .auth-description {
        margin: 0;
        color: var(--color-neutral-2);
        font-size: var(--font-size-sm);
      }

      .auth-confirmation {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-success-bg);
        border: 1px solid var(--color-success);
        border-radius: var(--border-radius-sm);
        color: var(--color-success);
        font-size: var(--font-size-sm);
      }

      .error-message {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-error-bg);
        border: 1px solid var(--color-error-border);
        border-radius: var(--border-radius-sm);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }

      .auth-footer {
        text-align: center;
        margin-top: var(--spacing-md);
      }

      .link {
        color: var(--color-primary-alt);
        text-decoration: none;
        font-weight: var(--font-weight-medium);
      }

      .link:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class ForgotPasswordComponent {
  protected readonly authService = inject(AuthService);
  protected readonly submitted = signal(false);

  readonly forgotForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  async onSubmit(): Promise<void> {
    if (!this.forgotForm.valid) {
      this.forgotForm.markAllAsTouched();
      return;
    }

    this.authService.clearError();
    const email = this.forgotForm.value.email;
    if (!email) {
      return;
    }

    const ok = await this.authService.requestPasswordReset(email);
    if (ok) {
      this.submitted.set(true);
    }
  }
}
